import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function DoctorManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", specialization: "", profile_image: "", bio: "", is_verified: false, hospital: "" });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/doctors").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.specialization) { toast.error("Name and specialization required"); return; }
    try {
      if (editId) await api.put(`/doctors/${editId}`, form);
      else await api.post("/doctors", form);
      toast.success(editId ? "Updated" : "Created");
      setOpen(false); setForm({ name: "", specialization: "", profile_image: "", bio: "", is_verified: false, hospital: "" }); setEditId(null); load();
    } catch { toast.error("Failed to save"); }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, specialization: item.specialization, profile_image: item.profile_image || "", bio: item.bio || "", is_verified: item.is_verified || false, hospital: item.hospital || "" });
    setEditId(item.id); setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/doctors/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="doctor-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Doctors</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={() => { setForm({ name: "", specialization: "", profile_image: "", bio: "", is_verified: false, hospital: "" }); setEditId(null); setOpen(true); }} data-testid="new-doctor-btn">
              <Plus className="h-4 w-4 mr-2" /> New Doctor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Edit" : "New"} Doctor</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label className="font-sans text-sm">Name *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} className="mt-1" data-testid="doctor-name" /></div>
              <div><Label className="font-sans text-sm">Specialization *</Label><Input value={form.specialization} onChange={e => set("specialization", e.target.value)} className="mt-1" data-testid="doctor-specialization" /></div>
              <div><Label className="font-sans text-sm">Hospital</Label><Input value={form.hospital} onChange={e => set("hospital", e.target.value)} className="mt-1" data-testid="doctor-hospital" /></div>
              <div><Label className="font-sans text-sm">Profile Image URL</Label><Input value={form.profile_image} onChange={e => set("profile_image", e.target.value)} className="mt-1" data-testid="doctor-image" /></div>
              <div><Label className="font-sans text-sm">Bio</Label><Textarea value={form.bio} onChange={e => set("bio", e.target.value)} rows={3} className="mt-1" data-testid="doctor-bio" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_verified} onCheckedChange={v => set("is_verified", v)} data-testid="doctor-verified" /><Label className="font-sans text-sm">Verified</Label></div>
              <Button onClick={handleSave} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-doctor"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(doc => (
          <div key={doc.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6" data-testid={`admin-doctor-${doc.id}`}>
            <div className="flex items-start gap-4">
              {doc.profile_image && <img src={doc.profile_image} alt={doc.name} className="w-16 h-16 rounded-full object-cover" />}
              <div className="min-w-0 flex-1">
                <h3 className="font-serif font-bold text-foreground">{doc.name}</h3>
                <p className="text-sm font-sans text-[#1E88E5]">{doc.specialization}</p>
                {doc.is_verified && <Badge className="mt-1 bg-[#26A69A] text-white text-[10px]"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(doc)} data-testid={`edit-doc-${doc.id}`}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
